import { useEffect, useRef, useState } from 'react';
import type { Kit } from '@/domain';
import { QueuedWhileOfflineError } from '@/application/outbox/dispatch';
import { useAddWorkProof } from '@/application/usecases/useKits';
import { openPhotoTile, type PhotoViewerItem } from '@/features/photos/photoViewerStore';
import { strings } from '@/shared/strings';
import { CameraIcon, CheckIcon, CloseIcon, PlusIcon, ReworkIcon } from '@/ui/icons';
import { BottomSheet } from '@/ui/primitives/BottomSheet';
import { sheetStyles } from '@/ui/primitives/sheetStyles';
import { Button } from '@/ui/primitives/Button';
import { toast } from '@/ui/primitives/toastStore';
import styles from './WorkProofSheet.module.css';

export interface WorkProofSheetProps {
  kit: Kit;
  /** Mandatory: no way past it, and the confirm waits for a photograph. */
  mandatory: boolean;
  /** The photographs are up, or safely queued, or skipped: the completion follows. */
  onDone: () => void;
  onCancel: () => void;
}

/** Enough to show the work from every side, few enough for a phone on lab Wi-Fi. */
export const PROOF_PHOTO_LIMIT = 6;

interface Shot {
  /** Also the upload's Idempotency-Key, kept so a retry writes the photograph once. */
  id: string;
  file: File;
  preview: string | null;
  sent: boolean;
}

/** A photograph in the sheet as the image viewer takes it: the file itself, with nobody's name on it yet. */
function localShot(shot: Shot, index: number): PhotoViewerItem {
  return {
    kind: 'local',
    id: shot.id,
    file: shot.file,
    caption: shot.sent ? strings.proof.photo(index + 1) : strings.viewer.notSentYet,
  };
}

function previewOf(file: File): string | null {
  // Not every runtime can make an object URL; the tile shows a camera instead.
  return typeof URL.createObjectURL === 'function' ? URL.createObjectURL(file) : null;
}

function release(shot: Shot): void {
  if (shot.preview !== null && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(shot.preview);
}

/**
 * Photographs of the finished work, taken at Mark done before the completion.
 * Section 2.4 of the request of 13 September 2026, rebuilt as a bottom sheet by
 * the request of 4 October 2026, section 6b.
 *
 * The camera opens straight from the sheet. Each photograph is a tile that
 * fits the width, with a button to take it again and one to remove it. The note
 * goes to Lab Notes with them. The confirm stays disabled until there is a
 * photograph; on optional, "Finish without one" is the way past.
 *
 * Before, not with, the completion: the photographs upload against the open
 * history entry and only then does the completion go, so a refused upload
 * leaves the case where it was and says why. In a dead spot they queue and the
 * completion queues behind them, which is what the outbox is for.
 *
 * It was a centred dialog whose footer would not wrap, and on a phone its
 * three buttons were wider than the dialog: Cancel was pushed off its left edge.
 * The shared sheet pins a footer that wraps, so it cannot happen here again.
 */
export function WorkProofSheet({ kit, mandatory, onDone, onCancel }: WorkProofSheetProps) {
  const add = useAddWorkProof();
  const camera = useRef<HTMLInputElement>(null);
  const retakeInput = useRef<HTMLInputElement>(null);
  const retaking = useRef<string | null>(null);
  const [shots, setShots] = useState<Shot[]>([]);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // The previews are the browser's to free, when the sheet goes.
  const live = useRef<Shot[]>([]);
  live.current = shots;
  useEffect(() => () => live.current.forEach(release), []);

  function added(file: File) {
    setError(null);
    const target = retaking.current;
    retaking.current = null;
    if (target !== null) {
      setShots((list) =>
        list.map((shot) => {
          if (shot.id !== target) return shot;
          release(shot);
          return { id: crypto.randomUUID(), file, preview: previewOf(file), sent: false };
        }),
      );
      return;
    }
    setShots((list) =>
      list.length >= PROOF_PHOTO_LIMIT
        ? list
        : [...list, { id: crypto.randomUUID(), file, preview: previewOf(file), sent: false }],
    );
  }

  function remove(id: string) {
    setShots((list) => {
      const gone = list.find((shot) => shot.id === id);
      if (gone !== undefined) release(gone);
      return list.filter((shot) => shot.id !== id);
    });
  }

  async function confirm() {
    if (shots.length === 0 || busy) return;
    setBusy(true);
    setError(null);
    let queued = false;
    const waiting = shots.filter((shot) => !shot.sent);
    try {
      for (const [index, shot] of waiting.entries()) {
        const last = index === waiting.length - 1;
        try {
          await add.mutateAsync({
            kitId: kit.id,
            file: shot.file,
            requestId: shot.id,
            // The note once, with the last photograph, so the Lab Notes row carries it.
            ...(last && note.trim() !== '' ? { note } : {}),
          });
        } catch (failure) {
          if (!(failure instanceof QueuedWhileOfflineError)) throw failure;
          queued = true;
        }
        setShots((list) => list.map((each) => (each.id === shot.id ? { ...each, sent: true } : each)));
      }
    } catch (failure) {
      // A refusal: nothing moves on, what was sent stays sent, and a retry sends the rest.
      setError(`${strings.proof.failed} ${(failure as Error).message}`);
      setBusy(false);
      return;
    }
    if (queued) toast(strings.sync.queued);
    setBusy(false);
    onDone();
  }

  const unsent = shots.some((shot) => !shot.sent) || note.trim() !== '';
  const full = shots.length >= PROOF_PHOTO_LIMIT;

  return (
    <BottomSheet
      title={`${strings.proof.title} · ${kit.jobOrderNo}`}
      lead={mandatory ? strings.proof.leadMandatory : strings.proof.leadOptional}
      onClose={onCancel}
      closeLabel={strings.proof.close}
      unsaved={
        unsent && !busy
          ? { question: strings.proof.unsaved, keep: strings.proof.keep, discard: strings.proof.discard }
          : null
      }
      footer={
        <div className={sheetStyles.actions}>
          {!mandatory && (
            <Button onClick={onDone} disabled={busy}>
              {strings.proof.skip}
            </Button>
          )}
          <Button
            variant="primary"
            className={sheetStyles.primary}
            icon={<CheckIcon />}
            onClick={() => void confirm()}
            disabled={busy || shots.length === 0}
          >
            {busy ? strings.proof.uploading : strings.proof.confirm}
          </Button>
        </div>
      }
    >
      {/*
        Two inputs and no visible one. The first opens the camera on a handset
        (capture="environment") and adds a photograph; the second replaces the
        one whose Retake was pressed.
      */}
      <input
        ref={camera}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        capture="environment"
        aria-label={strings.proof.inputLabel}
        className={styles.fileInput}
        onChange={(event) => {
          const picked = event.target.files?.[0];
          if (picked !== undefined) added(picked);
          // Cleared so picking the same file twice still fires a change.
          event.target.value = '';
        }}
      />
      <input
        ref={retakeInput}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        capture="environment"
        aria-label={strings.proof.retakeInputLabel}
        className={styles.fileInput}
        tabIndex={-1}
        onChange={(event) => {
          const picked = event.target.files?.[0];
          if (picked !== undefined) added(picked);
          event.target.value = '';
        }}
      />

      {shots.length === 0 ? (
        <button
          type="button"
          className={styles.take}
          onClick={() => camera.current?.click()}
          disabled={busy}
        >
          <CameraIcon size={28} />
          <span>{strings.proof.take}</span>
        </button>
      ) : (
        <>
          <p className={styles.count} aria-live="polite">
            {strings.proof.ready(shots.length)}
          </p>
          <ul className={styles.grid} data-photo-tiles="">
            {shots.map((shot, index) => (
              <li key={shot.id} className={styles.tile} aria-label={`${strings.proof.photo(index + 1)}, ${shot.file.name}`}>
                {/*
                  The photograph opens in the image viewer, from the file on the
                  device: a tile is too small to see whether the margin is in
                  focus before it is sent. Retake and remove stay beside this
                  button, never inside it. The request of 5 October 2026
                  (third), section 6.
                */}
                <button
                  type="button"
                  className={styles.open}
                  aria-label={strings.viewer.open(strings.proof.photo(index + 1))}
                  data-photo-tile={shot.id}
                  onClick={(event) => openPhotoTile(event, shots.map(localShot), index)}
                >
                  {shot.preview === null ? (
                    <span className={styles.blank} aria-hidden="true" data-photo-frame="">
                      <CameraIcon size={22} />
                    </span>
                  ) : (
                    <img src={shot.preview} alt="" draggable={false} data-photo-frame="" />
                  )}
                </button>
                {shot.sent ? (
                  <span className={styles.sent}>
                    <CheckIcon size={13} /> {strings.proof.sent}
                  </span>
                ) : (
                  <span className={styles.tools}>
                    <button
                      type="button"
                      aria-label={strings.proof.retakeOne(index + 1)}
                      disabled={busy}
                      onClick={() => {
                        retaking.current = shot.id;
                        retakeInput.current?.click();
                      }}
                    >
                      <ReworkIcon size={15} />
                    </button>
                    <button
                      type="button"
                      aria-label={strings.proof.remove(index + 1)}
                      disabled={busy}
                      onClick={() => remove(shot.id)}
                    >
                      <CloseIcon size={15} />
                    </button>
                  </span>
                )}
              </li>
            ))}
            {!full && (
              <li className={styles.tile}>
                <button
                  type="button"
                  className={styles.more}
                  onClick={() => camera.current?.click()}
                  disabled={busy}
                >
                  <PlusIcon size={20} />
                  <span>{strings.proof.another}</span>
                </button>
              </li>
            )}
          </ul>
          {full && <p className={styles.hint}>{strings.proof.limit(PROOF_PHOTO_LIMIT)}</p>}

          <label className={styles.label} htmlFor="proof-note">
            {strings.proof.note}
          </label>
          <textarea
            id="proof-note"
            className={styles.note}
            rows={2}
            value={note}
            disabled={busy}
            placeholder={strings.proof.notePlaceholder}
            onChange={(event) => setNote(event.target.value)}
          />
        </>
      )}

      {error !== null && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
    </BottomSheet>
  );
}
